import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/use-auth";
import { useWebSocket } from "@/hooks/use-websocket";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";
import { ProtectedRoute } from "@/components/protected-route";
import { ErrorBoundary } from "@/components/error-boundary";
import Home from "@/pages/home";
import EventRouter from "@/components/event-router";
import Login from "@/pages/login";
import NotFound from "@/pages/not-found";
import OrganisationsPage from "@/pages/organisations";
import SignupPage from "@/pages/signup";
import FeedbackPage from "@/pages/feedback";
import EventFeedback from "@/pages/event-feedback";
import AnalyticsDashboard from "@/pages/analytics-dashboard";
import PerformerHistory from "@/pages/performer-history";
import ActManagement from "@/pages/acts";
import ActSummary from "@/pages/acts/act-summary";
import EventSummary from "@/pages/events/event-summary";
import BookingSummary from "@/pages/bookings/booking-summary";
import BookingsCalendar from "@/pages/bookings/calendar";
import ManagementSelection from "@/pages/management-selection";
import BookingManagement from "@/pages/booking-management";
import EventManagement from "@/pages/event-management";
import ParticipantsPage from "@/pages/participants";
import BookingConfirmation from "@/pages/booking-confirmation";
import BookingAnalytics from "@/pages/booking-analytics";
import { EventFeed } from "@/pages/event-feed";

function Router() {
  // Initialize WebSocket for real-time updates
  useWebSocket();
  
  // Restore scroll position to top on route changes
  useScrollRestoration();
  
  return (
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/signup/:token">
        {(params) => <SignupPage />}
      </Route>
      <Route path="/feedback/:feedbackUrl">
        {(params) => <FeedbackPage />}
      </Route>
      <Route path="/booking-confirmation/:confirmationUrl">
        {(params) => <BookingConfirmation />}
      </Route>
      <Route path="/event-feedback/:eventId">
        {(params) => (
          <ProtectedRoute>
            <EventFeedback />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/">
        <ProtectedRoute>
          <ErrorBoundary>
            <Home />
          </ErrorBoundary>
        </ProtectedRoute>
      </Route>
      <Route path="/management-selection">
        <ProtectedRoute>
          <ErrorBoundary>
            <ManagementSelection />
          </ErrorBoundary>
        </ProtectedRoute>
      </Route>
      <Route path="/booking-management">
        <ProtectedRoute>
          <ErrorBoundary>
            <BookingManagement />
          </ErrorBoundary>
        </ProtectedRoute>
      </Route>
      <Route path="/event-management">
        <ProtectedRoute>
          <ErrorBoundary>
            <EventManagement />
          </ErrorBoundary>
        </ProtectedRoute>
      </Route>
      <Route path="/participants">
        <ProtectedRoute>
          <ParticipantsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/participants/:orgId">
        {(params) => (
          <ProtectedRoute>
            <ParticipantsPage />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/organisations">
        <ProtectedRoute>
          <OrganisationsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/analytics/:organisationId">
        {(params) => (
          <ProtectedRoute>
            <AnalyticsDashboard />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/booking-analytics/:organisationId">
        {(params) => (
          <ProtectedRoute>
            <BookingAnalytics />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/organisations/:orgId/acts">
        {(params) => (
          <ProtectedRoute>
            <ActManagement />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/organisations/:orgId/acts/:actId/summary">
        {(params) => (
          <ProtectedRoute>
            <ActSummary />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/organisations/:orgId/events/:eventId/summary">
        {(params) => (
          <ProtectedRoute>
            <EventSummary />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/organisations/:orgId/bookings/calendar">
        {(params) => (
          <ProtectedRoute>
            <BookingsCalendar />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/organisations/:orgId/bookings/:bookingId">
        {(params) => (
          <ProtectedRoute>
            <BookingSummary />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/analytics/:organisationId/performer/:participantId">
        {(params) => (
          <ProtectedRoute>
            <PerformerHistory />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/event/:id/participants">
        {(params) => (
          <ProtectedRoute>
            <EventRouter eventId={parseInt(params.id)} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/event/:id/focused/:viewType">
        {(params) => (
          <ProtectedRoute>
            <EventRouter eventId={parseInt(params.id)} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/event/:id/focused">
        {(params) => (
          <ProtectedRoute>
            <EventRouter eventId={parseInt(params.id)} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/events/:eventId/feed">
        {(params) => (
          <ProtectedRoute>
            <EventFeed />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/event/:id/:tab">
        {(params) => (
          <ProtectedRoute>
            <EventRouter eventId={parseInt(params.id)} activeTab={params.tab} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/event/:id">
        {(params) => (
          <ProtectedRoute>
            <EventRouter eventId={parseInt(params.id)} />
          </ProtectedRoute>
        )}
      </Route>
      <Route>
        <ProtectedRoute>
          <NotFound />
        </ProtectedRoute>
      </Route>
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <Toaster />
          <Router />
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
